import React, { useState, useEffect } from 'react';
import {
  Sparkles,
  X,
  Check,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  ShieldCheck,
  Copy,
  Zap,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { playerProfileService } from '../../apis/playerProfileService';

const PURPOSE_PRESETS = [
  'Tournament Announcement / Structure',
  'Cash Game Action / Tables Running',
  'Promotional Special / Bonus Chips',
  'Club Hours / Holiday Notice',
  'Player Account / Membership Notice',
  'General Club Broadcast',
];

export default function SmsMessageOptimizerModal({
  isOpen,
  onClose,
  initialMessage = '',
  onApplyMessage,
  recipientContext = 'Active poker club members in McAllen, Texas',
}) {
  const [draftMessage, setDraftMessage] = useState(initialMessage || '');
  const [purpose, setPurpose] = useState(PURPOSE_PRESETS[0]);
  const [customPurpose, setCustomPurpose] = useState('');
  const approvedUseCase = 'Customer Care / Tournament Updates';
  const [characterLimit, setCharacterLimit] = useState('160 GSM-7');
  
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDraftMessage(initialMessage || '');
      setResult(null);
      setCopied(false);
    }
  }, [isOpen, initialMessage]);

  if (!isOpen) return null;

  const handleRunOptimizer = async () => {
    if (!draftMessage.trim()) {
      toast.error('Please enter a message to optimize');
      return;
    }

    setLoading(true);
    try {
      const selectedPurpose = purpose === 'custom' ? customPurpose : purpose;
      const res = await playerProfileService.optimizeSmsMessage({
        message: draftMessage,
        purpose: selectedPurpose || 'Club notification',
        recipientContext,
        approvedUseCase,
        characterLimit,
      });

      if (res?.success && res?.data?.data) {
        setResult(res.data.data);
        toast.success('Message optimized with Claude!');
      } else {
        toast.error(res?.error || 'Failed to optimize message');
      }
    } catch (err) {
      console.error('Optimizer Error:', err);
      toast.error(err.message || 'Error communicating with Claude API');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (!result?.rewritten_message) return;
    onApplyMessage(result.rewritten_message);
    toast.success('Optimized message applied to draft!');
    onClose();
  };

  const handleCopy = () => {
    if (!result?.rewritten_message) return;
    navigator.clipboard.writeText(result.rewritten_message);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-[80] p-3 sm:p-4 animate-in fade-in duration-150">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] flex flex-col overflow-hidden border border-gray-800 animate-in zoom-in-95 duration-150">
        
        {/* MODAL HEADER: Black background, white text with yellow/amber accents */}
        <div className="px-5 sm:px-6 py-4 bg-black text-white flex items-center justify-between border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-400/20 border border-amber-400/40 flex items-center justify-center text-amber-400 shadow-inner">
              <Sparkles size={20} className="animate-pulse" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-white tracking-wide">
                  Claude AI SMS Optimizer
                </h3>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-400/20 text-amber-300 border border-amber-400/40">
                  Haiku 3.5
                </span>
              </div>
              <p className="text-xs text-gray-300 font-normal">
                Elite Poker Lounge • 160-char SMS optimizer & carrier compliance guard
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-amber-400 transition-colors p-1.5 rounded-lg hover:bg-white/10"
            title="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* MODAL BODY */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5 text-xs bg-gray-50/60">
          
          {/* DRAFT INPUT SECTION */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="font-bold text-gray-800 flex items-center gap-1.5 text-xs">
                <span>Original Message Draft</span>
                <span className="text-gray-400 font-normal font-mono">
                  ({draftMessage.length} chars)
                </span>
              </label>
              {draftMessage !== initialMessage && (
                <button
                  type="button"
                  onClick={() => setDraftMessage(initialMessage)}
                  className="text-[11px] text-amber-700 hover:text-amber-900 font-medium underline"
                >
                  Reset to original draft
                </button>
              )}
            </div>
            <textarea
              value={draftMessage}
              onChange={(e) => setDraftMessage(e.target.value)}
              rows={4}
              placeholder="Paste or write your SMS message here to optimize..."
              className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-xl text-xs text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 font-sans resize-y shadow-inner"
            />
          </div>

          {/* PARAMETERS GRID */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3.5 bg-white rounded-xl border border-gray-200/90 shadow-sm">
            <div>
              <label className="font-semibold text-gray-700 block mb-1">Message Purpose</label>
              <select
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                className="w-full px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-800 focus:outline-none focus:ring-1 focus:ring-amber-500"
              >
                {PURPOSE_PRESETS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
                <option value="custom">Custom purpose...</option>
              </select>
              {purpose === 'custom' && (
                <input
                  type="text"
                  value={customPurpose}
                  onChange={(e) => setCustomPurpose(e.target.value)}
                  placeholder="Specify message intent..."
                  className="mt-1.5 w-full px-2.5 py-1 bg-white border border-gray-200 rounded text-xs focus:ring-1 focus:ring-amber-500 focus:outline-none"
                />
              )}
            </div>

            <div>
              <label className="font-semibold text-gray-700 block mb-1">Target Limit</label>
              <select
                value={characterLimit}
                onChange={(e) => setCharacterLimit(e.target.value)}
                className="w-full px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-800 focus:outline-none focus:ring-1 focus:ring-amber-500"
              >
                <option value="160 GSM-7">160 GSM-7 characters (1 single SMS segment)</option>
                <option value="320 GSM-7">Up to 320 characters (2 segments max)</option>
                <option value="Concise and natural without strict limit">Concise natural length (No hard limit)</option>
              </select>
            </div>
          </div>

          {/* RUN OPTIMIZER BUTTON: Yellow with black text */}
          <div className="flex justify-center">
            <button
              type="button"
              disabled={loading || !draftMessage.trim()}
              onClick={handleRunOptimizer}
              className="px-6 py-2.5 bg-amber-400 hover:bg-amber-500 disabled:opacity-50 text-black font-bold rounded-xl shadow-md flex items-center gap-2 transition-all transform hover:-translate-y-0.5 text-xs"
            >
              {loading ? (
                <>
                  <Loader2 size={15} className="animate-spin text-black" />
                  <span>Claude is rewriting & checking policies...</span>
                </>
              ) : (
                <>
                  <Sparkles size={15} className="text-black" />
                  <span>{result ? 'Re-Optimize with Claude' : 'Optimize SMS with Claude'}</span>
                </>
              )}
            </button>
          </div>

          {/* RESULTS DISPLAY */}
          {result && (
            <div className="space-y-4 pt-2 border-t border-gray-200 animate-in fade-in duration-200">
              
              {/* SAVINGS & METRICS BANNER */}
              <div className="p-3.5 bg-amber-50/80 rounded-xl border border-amber-200 flex flex-wrap items-center justify-between gap-3 text-xs">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-black text-amber-400 flex items-center justify-center font-bold">
                    <Zap size={16} />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">
                      Optimized Length: <span className="font-mono text-amber-700 font-bold">{result.character_count}</span> characters
                    </div>
                    <div className="text-[11px] text-gray-600">
                      Segments: <strong className="font-mono text-gray-900">{result.stats?.rewritten_segments || 1}</strong> (Originally {result.stats?.original_segments || 1})
                    </div>
                  </div>
                </div>

                {result.stats?.chars_saved > 0 && (
                  <div className="px-3 py-1 rounded-full bg-emerald-100 border border-emerald-300 text-emerald-800 font-bold text-xs flex items-center gap-1.5">
                    <CheckCircle2 size={13} />
                    <span>Saved {result.stats.chars_saved} chars ({result.stats.segments_saved > 0 ? `Saved ${result.stats.segments_saved} segment(s)!` : 'More concise'})</span>
                  </div>
                )}
              </div>

              {/* COMPLIANCE CHECK CARD */}
              {result.compliance_review_needed ? (
                <div className="p-3.5 bg-amber-50 rounded-xl border border-amber-300 text-amber-900 space-y-1">
                  <div className="font-bold flex items-center gap-1.5 text-xs text-amber-900">
                    <AlertTriangle size={15} className="text-amber-600 flex-shrink-0" />
                    <span>Carrier / Provider Policy Review Recommended</span>
                  </div>
                  <p className="text-[11px] text-amber-800 leading-relaxed pl-5">
                    {result.review_reason || 'Carrier 10DLC policies require explicit opt-in confirmation for marketing content.'}
                  </p>
                </div>
              ) : (
                <div className="p-2.5 bg-emerald-50 rounded-xl border border-emerald-200 text-emerald-900 flex items-center gap-2 text-xs font-semibold">
                  <ShieldCheck size={16} className="text-emerald-600 flex-shrink-0" />
                  <span>Natural Tone & Factual Details Preserved • Ready for Broadcast</span>
                </div>
              )}

              {/* REWRITTEN MESSAGE CARD */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="font-bold text-gray-900 flex items-center gap-1.5">
                    <Sparkles size={13} className="text-amber-600" />
                    <span>Optimized SMS Preview:</span>
                  </label>
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="text-gray-600 hover:text-gray-900 font-semibold flex items-center gap-1 text-[11px]"
                  >
                    {copied ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
                    <span>{copied ? 'Copied' : 'Copy Text'}</span>
                  </button>
                </div>

                <div className="p-3.5 bg-amber-50/20 rounded-xl border-2 border-amber-300 shadow-sm text-gray-900 font-sans text-xs whitespace-pre-wrap leading-relaxed select-all">
                  {result.rewritten_message || (
                    <span className="text-gray-400 italic">No rewritten text returned (review needed).</span>
                  )}
                </div>
              </div>

            </div>
          )}

        </div>

        {/* MODAL FOOTER */}
        <div className="px-5 sm:px-6 py-3.5 bg-gray-100/90 border-t border-gray-200 flex items-center justify-between text-xs">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold rounded-xl transition-colors shadow-sm"
          >
            Cancel
          </button>

          {result?.rewritten_message && (
            <button
              type="button"
              onClick={handleApply}
              className="px-5 py-2 bg-amber-400 hover:bg-amber-500 text-black font-bold rounded-xl shadow-md flex items-center gap-2 transition-all transform hover:-translate-y-0.5"
            >
              <Check size={14} />
              <span>Apply to Message Composer</span>
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
